import type { BetSelection, QuaddieType } from "@/features/betslip/types";
import {
  outcomeType as outcomeTypeSchema,
  type OutcomeType,
} from "@/hooks/firestore/models/outcome";
import { toTitleCaseWithExclusions } from "@/utilities/racingUtilities";
import type { ProcessedOutcome } from "../builders/types";

/**
 * Resolve the displayable odds for a selection, taking any pending odds
 * change into account. `pendingOdds` is the value from
 * `state.oddsChanges[outcomeId]` (number when present, undefined when
 * not). Once the user accepts, the change is baked into selection.odds
 * and the entry is gone — at which point this returns selection.odds.
 */
export const getCurrentOdds = (
  selection: BetSelection,
  pendingOdds?: number,
): number => pendingOdds ?? selection.odds;

/**
 * Selections store `outcomeType` as a plain string (legacy SubOutcomesType
 * ancestry), but `OutcomeIcon` takes the zod `OutcomeType` union. Validate
 * rather than cast; unknown values fall back to the avatar rendering.
 */
export const toOutcomeType = (value?: string): OutcomeType | undefined => {
  const parsed = outcomeTypeSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
};

export const getQuaddieTitle = (type: QuaddieType): string => {
  if (type === "LATE") return "Quaddie";
  if (type === "EARLY") return "Early Quaddie";
  return toTitleCaseWithExclusions((type || "").replace("_", " "));
};

/**
 * Count combinations across quaddie races. Total combos = product of
 * the number of selected runners per race.
 */
export const countQuaddieCombos = (
  quaddieSelections: Record<string, { outcomeId: string }[]>,
): number =>
  Object.values(quaddieSelections).reduce(
    (combos, runners) => combos * (runners?.length || 0),
    1,
  );

// SEM legs display in market-first order rather than the order the user
// picked them. Racing multis follow the finishing hierarchy (Win, then
// Top 2/3/4); equal market types keep their incoming order, which preserves
// the runner-number sequence set upstream.
const srmMarketTypePriority: Record<string, number> = {
  RUNNER_WIN: 0,
  RUNNER_TOP_2: 1,
  RUNNER_TOP_3: 2,
  RUNNER_TOP_4: 3,
};

export const sortSemLegs = <Leg extends { outcomeType?: string }>(
  legs: Leg[],
  isSrm: boolean,
): Leg[] => {
  // Sports multis keep pick order — their legs span unrelated markets with
  // no natural hierarchy to sort by.
  if (!isSrm) return legs;
  if (!legs.some((leg) => leg.outcomeType)) return legs;
  return [...legs].sort((a, b) => {
    const priorityA = srmMarketTypePriority[a.outcomeType ?? ""] ?? 999;
    const priorityB = srmMarketTypePriority[b.outcomeType ?? ""] ?? 999;
    return priorityA - priorityB;
  });
};

/**
 * `decorateOutcomes` swaps PLAYER_HOME/AWAY competitorIds for the team's id
 * so the avatar renders, this helper ensures we get the correct competitorId
 * the backend matches on
 */
export const getActualCompetitorId = (
  outcome:
    | Pick<ProcessedOutcome, "competitorId" | "actualCompetitorId">
    | undefined,
): string | undefined => outcome?.actualCompetitorId ?? outcome?.competitorId;
